import { Container, Grid, GridFive, GridHalf } from '@troon/ui';
import { Show, Suspense, Match, Switch, createSignal } from 'solid-js';
import { cachedGet } from '@troon/api-client';
import { createAsync, useNavigate, Navigate, useSearchParams } from '@solidjs/router';
import { TroonCardSubscriptionAddonType } from '../../../graphql';
import { UnlimitedGuestPassesInfoCard } from '../../../components/addons/info-cards/unlimited-guest-passes';
import { useUser } from '../../../providers/user';
import AddOnPurchaseForm from '../../../components/addons/addon-purchase-form';

import PurchaseConfirmation from '../../../components/addons/purchase-confirmation';
import type { ApiResponse } from '@troon/api-client';

const getAddOnsProducts = cachedGet('/v0/subscription-addons/products');

export default function UnlimitedGuestPassesPage() {
	const addOns = createAsync(() => getAddOnsProducts({}));
	const [purchaseSuccess, setPurchaseSuccess] = createSignal<ApiResponse<'post', '/v0/subscription-addons/purchase'>>();
	const user = useUser();
	const navigate = useNavigate();
	const unlimitedGuestPassProduct = () => {
		const products = addOns()?.products;
		if (!products) {
			return undefined;
		}
		return products.find((p) => p.type === TroonCardSubscriptionAddonType.UnlimitedGuestPasses);
	};

	const [searchParams] = useSearchParams();
	const reservationId = searchParams.reservationId;
	return (
		<Show when={user()?.troonAccessProductType} fallback={<Navigate href="/membership" />}>
			<Show
				when={!user()?.subscription?.productAddOns.includes(TroonCardSubscriptionAddonType.UnlimitedGuestPasses)}
				fallback={<Navigate href="/account/subscription" />}
			>
				<Container size="default" class="py-4 md:py-10">
					<Switch>
						<Match when={purchaseSuccess()?.stripeSubscription}>
							<PurchaseConfirmation
								subscription={purchaseSuccess()!.stripeSubscription}
								userEmail={user()?.email}
								onClose={() => {
									navigate(reservationId ? `/reservation/${reservationId}` : '/');
								}}
								closeText={`${reservationId ? 'Continue to reservation' : 'Find Tee Times'}`}
							/>
						</Match>
						<Match when={!purchaseSuccess()}>
							<Grid>
								<GridHalf class="order-2 md:order-1">
									<Show when={user()?.subscription && unlimitedGuestPassProduct()}>
										{(productAddon) => {
											return (
												<AddOnPurchaseForm
													addOnProduct={productAddon()}
													onPurchaseSuccess={(data) => setPurchaseSuccess(data)}
												/>
											);
										}}
									</Show>
								</GridHalf>
								<GridFive class="order-1 md:order-2 md:ml-8">
									<Suspense fallback={<InfoCardSkeleton />}>
										<Show when={user()?.subscription && unlimitedGuestPassProduct()}>
											{(productAddon) => {
												return <UnlimitedGuestPassesInfoCard productAddon={productAddon()} />;
											}}
										</Show>
									</Suspense>
								</GridFive>
							</Grid>
						</Match>
					</Switch>
				</Container>
			</Show>
		</Show>
	);
}

export const route = { info: { nav: { hero: false } } };

function InfoCardSkeleton() {
	return (
		<div class="animate-pulse space-y-4 rounded-lg border bg-white p-6">
			<div class="h-6 w-1/3 rounded-lg bg-neutral-200" />
			<div class="h-8 w-2/3 rounded-lg bg-neutral-200" />
			<div class="h-4 w-full rounded-lg bg-neutral-200" />
			<div class="h-4 w-1/2 rounded-lg bg-neutral-200" />
			<div class="h-10 w-1/2 rounded-lg bg-neutral-200" />
		</div>
	);
}
